import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import { Dispensary } from "custom-types/Dispensary";
import useDomain from "hooks/useDomain";
import { DispensaryPageType, getMetadataInfo } from "lib/dispensaryMetadata";
import { getLocationState } from "redux/selectors/location";

type DispensaryHeadProps = {
  dispensary: Dispensary;
  pageType: DispensaryPageType;
  noIndex?: boolean;
};

const getCanonicalUrl = (currentPath: string) => {
  const [canonicalUrl, queryString] = currentPath.split("?");

  const query = new URLSearchParams(queryString);
  const page = Number(query.get("page")) || 1;

  if (canonicalUrl.endsWith("/menu")) {
    return canonicalUrl.replace(/\/menu$/, "");
  }

  if (canonicalUrl.endsWith("/reviews") && page > 1) {
    return `${canonicalUrl}?page=${page}`;
  }

  return canonicalUrl;
};

const DispensaryHead: React.FC<DispensaryHeadProps> = ({
  dispensary,
  pageType,
  noIndex,
}) => {
  const { countryCode } = useSelector(getLocationState);

  const router = useRouter();
  const baseUrl = useDomain();
  const currentPath = `${baseUrl}${router.asPath}`;

  const pageNumber = Number(router.query.page);

  const { pageDescription, socialTitle, pageImage } = getMetadataInfo({
    countryCode,
    dispensary,
    pageNumber,
    pageType,
  });

  const twitterTags = [
    { content: socialTitle, name: "twitter:title" },
    { content: pageDescription, name: "twitter:description" },
    { content: pageImage, name: "twitter:image" },
  ];

  const ogTags = [
    { content: socialTitle, property: "og:title" },
    { content: pageDescription, property: "og:description" },
    { content: pageImage, property: "og:image" },
  ];

  return (
    <Head>
      <meta property="og:url" content={currentPath} key="og:url" />
      {noIndex && <meta name="robots" content="noindex,noarchive" />}
      <title>{socialTitle}</title>
      <meta
        name="description"
        content={pageDescription}
        key="meta-description"
      />
      {ogTags.map((tag) => (
        <meta {...tag} key={`meta-${tag.property}`} />
      ))}
      {twitterTags.map((tag) => (
        <meta {...tag} key={`meta-${tag.name}`} />
      ))}
      <link
        rel="canonical"
        href={getCanonicalUrl(currentPath)}
        key="canonical"
      />
    </Head>
  );
};

export default DispensaryHead;
